<template>
  <div class="inline-flex">
    <Dropdown ref="picker" stickLeft>
      <template #wrapper>
        <SimpleButton
          size="sm"
          :disabled="!canPick"
          :class="focusedLabel
            ? 'border-accent! bg-accent-soft! text-accent!'
            : 'hover:text-accent!'"
          :title="$t('tags.tag_focus_hotkey')"
        >
          <IconCircleDot class="flex-none size-3.5" />
          <span
            v-if="focusedLabel"
            class="truncate max-w-[8.75rem] max-sm:max-w-[3.75rem]"
          >{{ focusedLabel.name }}</span>
          <span v-else class="max-sm:hidden">{{ $t('tags.tag_focus') }}</span>
          <IconX
            v-if="focusedLabel"
            class="flex-none size-3.5 opacity-70 hover:opacity-100"
            :title="$t('tags.unfocus')"
            @click.stop="clearFocus"
          />
        </SimpleButton>
      </template>
      <template #items>
        <AppSearchBox
          class="w-[16rem] max-sm:w-[calc(100vw-2.5rem)]"
          :items="sorted"
          :getLabel="tag => tag.name"
          :placeholder="$t('tags.filter_tags')"
          @select="select"
        >
          <template #header>
            <div v-if="focusedLabel" class="flex items-center gap-2">
              <SimpleButton variant="accent" size="sm" class="flex-1" @click.stop="openEta" :title="$t('tags.show_eta')">
                <IconClock class="flex-none size-3.5" />
                {{ $t('tags.calculate_eta') }}
              </SimpleButton>
              <SimpleButton size="sm" :pressed="exportOpen" @click.stop="exportOpen = !exportOpen">
                <IconUpload class="flex-none size-3.5" />
                {{ $t('reports.export') }}
              </SimpleButton>
            </div>
            <div
              v-if="focusedLabel && exportOpen"
              class="mt-2 flex flex-col gap-2.5 rounded-card border border-line bg-input p-3"
            >
              <div class="text-label font-medium tracking-wide text-text uppercase">{{ $t('tags.export_settings') }}</div>
              <div class="flex items-center justify-between gap-2 text-body text-text">
                <label for="includeExecutor">{{ $t('tags.include_executor') }}</label>
                <ToggleSwitch id="includeExecutor" v-model="optIncludeExecutor" />
              </div>
              <div class="flex items-center justify-between gap-2 text-body text-text">
                <label for="hoursInSP">{{ $t('tags.hours_in_story_point') }}</label>
                <SingleLineInput id="hoursInSP" class="w-16" type="number" step="any" v-model="optHoursInSP" :noButtons="false" />
              </div>
              <div class="flex items-center justify-between gap-2 text-body text-text">
                <label>{{ $t('tags.round_up_to') }}</label>
                <div class="flex items-center gap-1">
                  <SimpleButton
                    v-for="opt in ROUND_UP_OPTIONS"
                    :key="opt.minutes"
                    size="sm"
                    class="min-w-11"
                    :pressed="optRoundUpMinutes === opt.minutes"
                    @click.stop="optRoundUpMinutes = opt.minutes"
                  >{{ opt.label }}</SimpleButton>
                </div>
              </div>
              <div
                class="flex items-center justify-between gap-2 text-body text-text"
                :title="$t('tags.include_doing_hint')"
              >
                <label for="includeDoing">{{ $t('tags.include_doing') }}</label>
                <ToggleSwitch id="includeDoing" v-model="optIncludeDoing" />
              </div>
              <div
                class="flex items-center justify-between gap-2 text-body text-text"
                :class="{ 'opacity-60': !optIncludeDoing }"
              >
                <label>{{ $t('tags.notdoing_tag') }}</label>
                <DropdownListSimple
                  v-model="optNotDoingLabelId"
                  :singleSelect="true"
                  :placeholder="$t('tags.select_label')"
                  :inputData="notDoingOptions"
                  :disabled="!optIncludeDoing"
                  class="w-40"
                >
                  <template #item="{ option }">
                    <div
                      class="tag-tint shrink min-w-5 h-4.5 px-2 py-0.5 leading-none
                             flex items-center justify-center
                             rounded-lg font-bold text-unit overflow-hidden"
                      :style="{ '--tag-color': `#${option.color}` }"
                    >
                      <div class="truncate" :title="option.label">{{ option.label }}</div>
                    </div>
                  </template>
                </DropdownListSimple>
              </div>
              <SimpleButton
                class="mt-0.5 w-full"
                variant="accent"
                size="lg"
                :loading="exporting"
                :disabled="exporting"
                @click.stop="onExportTasks"
              >
                <IconTableExport v-if="!exporting" class="size-3.5 flex-none" />
                {{ $t('reports.export_to_gsheets') }}
              </SimpleButton>
            </div>
          </template>
          <template #item="{ item }">
            <div class="flex items-center gap-2 w-full">
              <div
                class="tag-tint shrink min-w-5 h-4.5 px-2 py-0.5 leading-none
                       flex items-center justify-center
                       rounded-lg font-bold text-unit overflow-hidden"
                :style="{ '--tag-color': `#${item.color}` }"
              >
                <div class="truncate" :title="item.name">{{ item.name }}</div>
              </div>
              <div class="ml-auto text-unit text-text-soft">{{ tagCounts[item.id] || 0 }}</div>
              <IconCheck
                v-if="item.id === focusedLabel?.id"
                class="flex-none size-3.5 text-accent"
              />
            </div>
          </template>
          <template #empty>
            <div class="px-2.5 pb-2 text-unit text-text">{{ $t('addTask.no_tags') }}</div>
          </template>
        </AppSearchBox>
      </template>
    </Dropdown>
  </div>
  <TagEtaPopup
    v-if="focusedLabel && project?.id"
    :open="etaOpen"
    :tag="focusedLabel"
    :project-id="project.id"
    @update:open="(v) => etaOpen = v"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useMagicKeys } from '@vueuse/core';
import { useStore } from 'vuex';
import { callAPI } from '@/store/socket';
import Dropdown from '@/components/elements/Dropdown';
import DropdownListSimple from '@/components/elements/DropdownListSimple';
import SimpleButton from '@/components/elements/SimpleButton';
import SingleLineInput from '@/components/elements/SingleLineInput';
import AppSearchBox from '@/components/elements/AppSearchBox';
import ToggleSwitch from '@/components/elements/ToggleSwitch';
import { IconCircleDot, IconClock, IconUpload, IconX, IconCheck, IconTableExport } from '@iconify-prerendered/vue-tabler';
import TagEtaPopup from '@/components/widgets/TagEtaPopup.vue';



const ROUND_UP_OPTIONS = [
  { minutes: 60, label: '1h' },
  { minutes: 30, label: '30m' },
  { minutes: 10, label: '10m' },
];

const store = useStore();

const project = computed(() => store.state.project.project);
const user = computed(() => store.state.user.user);

const currentMember = computed(() => {
  if (!project.value?.projectMembers) return null;
  return project.value.projectMembers.find(pm => pm.userId === user.value.id) || null;
});

const labels = computed(() => (project.value?.labels || []).filter(l => !l.archived));

const focusedLabel = computed(() => {
  if (!currentMember.value?.tagFocus) return null;
  return labels.value.find(l => l.id === currentMember.value.tagFocus) || null;
});

const canPick = computed(() => !!project.value && !!labels.value.length);

// Dropdown state
const picker = ref(null);

// how many tasks carry each tag, shown next to the tag in the picker
const tagCounts = computed(() => {
  const counts = {};
  (project.value?.stages || []).forEach(stage => (stage.tasks || []).forEach((task) => {
    (task.labels || []).forEach((label) => {
      counts[label.id] = (counts[label.id] || 0) + 1;
    });
  }));
  return counts;
});

const sorted = computed(() => {
  // Sort by updatedAt desc, then createdAt desc, fallback to name
  const arr = [...labels.value];
  arr.sort((a, b) => {
    const ad = new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
    if (ad !== 0) return ad;
    return (a.name || '').localeCompare(b.name || '');
  });
  return arr;
});

function select(tag) {
  if (!currentMember.value) {
    return;
  }
  // clicking the already focused tag unfocuses it
  const tagFocus = tag && tag.id !== focusedLabel.value?.id ? tag.id : null;
  store.dispatch('project/actionChangeProjectMemberTagFocus', {
    projectMemberId: currentMember.value.id,
    tagFocus,
    projectId: project.value.id,
  });
  picker.value?.close();
}

function clearFocus() {
  select(null);
}

// ETA popup state
const etaOpen = ref(false);
function openEta() {
  etaOpen.value = true;
  picker.value?.close();
}

// Export panel state
const exportOpen = ref(false);
const optIncludeExecutor = ref(true);
const optHoursInSP = ref('1');
const optIncludeDoing = ref(true);
const exporting = ref(false);
const optRoundUpMinutes = ref(60);
const optNotDoingLabelId = ref(null);
const LS_TAG_EXPORT_OPTS = 'tag_focus_export_opts';

function loadExportOptions() {
  try {
    const raw = localStorage.getItem(LS_TAG_EXPORT_OPTS);
    if (!raw) return;
    const opts = JSON.parse(raw);
    if (typeof opts.includeExecutor === 'boolean') optIncludeExecutor.value = opts.includeExecutor;
    if (opts.hoursInSP != null && opts.hoursInSP !== '') optHoursInSP.value = String(opts.hoursInSP);
    if (typeof opts.includeDoing === 'boolean') optIncludeDoing.value = opts.includeDoing;
    if (Number.isFinite(opts.roundUpMinutes)) optRoundUpMinutes.value = Number(opts.roundUpMinutes);
    if (opts.notDoingLabelId) optNotDoingLabelId.value = opts.notDoingLabelId;
  } catch (e) {
    console.error('Failed to load tag export options', e);
  }
}

const hoursInStoryPoint = computed(() => Math.max(0, parseFloat(optHoursInSP.value || '1') || 0));

function saveExportOptions() {
  try {
    const payload = {
      includeExecutor: !!optIncludeExecutor.value,
      hoursInSP: hoursInStoryPoint.value,
      includeDoing: !!optIncludeDoing.value,
      roundUpMinutes: Math.max(1, Number(optRoundUpMinutes.value) || 60),
      notDoingLabelId: optNotDoingLabelId.value || null,
    };
    localStorage.setItem(LS_TAG_EXPORT_OPTS, JSON.stringify(payload));
  } catch (e) {
    console.error('Failed to save tag export options', e);
  }
}

// Dropdown options for "Not doing" tag rendered via slot (no inline HTML strings)
const notDoingOptions = computed(() => (labels.value || []).map(l => ({
  id: l.id,
  label: l.name || '',
  color: l.color || '666666',
})));

watch([
  () => project.value?.id,
  () => project.value?.labels,
  optNotDoingLabelId,
], () => {
  if (!optNotDoingLabelId.value) return;
  if (!project.value || !Array.isArray(project.value.labels)) return;

  const hasMatchingLabel = project.value.labels.some(label => !label.archived && label.id === optNotDoingLabelId.value);
  if (!hasMatchingLabel) {
    optNotDoingLabelId.value = null;
  }
}, { immediate: true });

async function onExportTasks() {
  if (exporting.value) return;
  exporting.value = true;
  try {
    const resp = await callAPI({
      method: 'exportTagEstimateToGoogleSheets',
      params: {
        projectId: project.value.id,
        labelId: focusedLabel.value.id,
        hoursInStoryPoint: hoursInStoryPoint.value,
        roundUpMinutes: optRoundUpMinutes.value,
        includeExecutor: optIncludeExecutor.value,
        includeDoing: optIncludeDoing.value,
        notDoingLabelId: optNotDoingLabelId.value || undefined,
      },
      async: true,
      dontWaitIfNoConn: true,
    });
    if (resp && resp.success && resp.spreadsheetUrl) {
      window.open(resp.spreadsheetUrl, '_blank');
    } else {
      console.error('Tag export failed', resp);
    }
  } catch (e) {
    console.error('Tag export error', e);
  } finally {
    exporting.value = false;
  }
}

// Use VueUse for hotkeys
const keys = useMagicKeys({ 
  passive: false, 
});

watch(keys['alt+g'], async (pressed) => {
  if (pressed && !picker.value?.opened && canPick.value) {
    setTimeout(() => {
      picker.value?.activate();
    });
  }
});

watch(keys.esc, (pressed) => {
  if (pressed) {
    picker.value?.close();
  }
});

// Persist export options across sessions (workspace-agnostic)
onMounted(() => {
  loadExportOptions();
});
watch([optIncludeExecutor, optHoursInSP, optIncludeDoing, optRoundUpMinutes, optNotDoingLabelId], () => {
  saveExportOptions();
});



</script>
